/**
 * ATT-2872 admin half — per-row coerce for the discovery allowlist.
 *
 * Mirrors the shape of `lib/tenantSettings/coerce.ts` but uses the broader
 * `DiscoveryTenantSettingKey` registry (display_name + hide_languages +
 * feature_flags + logo_url). Drops rows whose key is not in the allowlist
 * silently (cross-portal union from the backend); captures a Sentry warning
 * on per-row Zod failure.
 */

import * as Sentry from "@sentry/nextjs";
import {
  DiscoveryTenantSettingKey,
  isDiscoveryAllowlistedKey,
  type DiscoveryTenantSettingKeyName,
} from "./registry";

export type CoercedDiscoverySetting = {
  key: DiscoveryTenantSettingKeyName;
  value: unknown;
};

export function coerceDiscoverySetting(
  key: string,
  value: unknown
): CoercedDiscoverySetting | null {
  if (!isDiscoveryAllowlistedKey(key)) return null;
  const schema = DiscoveryTenantSettingKey[key];
  const result = schema.safeParse(value);
  if (!result.success) {
    Sentry.withScope((scope) => {
      scope.setLevel("warning");
      scope.setTag("reason", "zod-validation");
      scope.setTag("setting_key", key);
      scope.setTag("source", "tenant-discovery");
      scope.setExtras({
        issues: result.error.issues.map((i) => ({ path: i.path, message: i.message })),
      });
      Sentry.captureMessage("tenant-discovery-validation-failure", { level: "warning" });
    });
    return null;
  }
  return { key, value: result.data };
}
